/**
 * Validated Context Form
 * 
 * Production-ready form for managing workflow context and variable scoping
 */

import {
    ActionIcon,
    Alert,
    Badge,
    Button,
    Card,
    Checkbox,
    Code,
    Group,
    JsonInput,
    Modal,
    Paper,
    Select,
    Stack,
    Table,
    Tabs,
    Text,
    Textarea,
    TextInput,
    Title,
    Tooltip
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
    IconAlertTriangle,
    IconCheck,
    IconCode,
    IconHierarchy,
    IconInfoCircle,
    IconPlus,
    IconTrash,
    IconVariable,
    IconX
} from '@tabler/icons-react';
import React, { useState } from 'react';
import { useContextMutations } from '../hooks/useWorkflowState';
import { useValidatedForm, useValidatedFormSubmission } from '../hooks/useValidatedForm';

interface ContextVariable {
    name: string;
    type: 'string' | 'number' | 'boolean' | 'object' | 'array';
    value: any;
    description?: string;
    required: boolean;
    readonly: boolean;
    encrypted: boolean;
}

interface ContextData {
    name: string;
    description?: string;
    scope: 'global' | 'workflow' | 'instance' | 'task';
    parentContext?: string;
    variables: ContextVariable[];
    inheritanceEnabled: boolean;
    overridePolicy: 'allow' | 'deny' | 'merge';
    validation?: {
        enabled: boolean;
        schema?: Record<string, any>;
    };
    metadata?: Record<string, any>;
}

interface ValidatedContextFormProps {
    initialData?: Partial<ContextData>;
    mode: 'create' | 'edit';
    onSuccess?: (data: ContextData) => void;
    onCancel?: () => void;
}

export const ValidatedContextForm: React.FC<ValidatedContextFormProps> = ({
    initialData,
    mode,
    onSuccess,
    onCancel
}) => {
    const { createContext, updateContext } = useContextMutations();
    const [activeTab, setActiveTab] = useState<string | null>('basic');
    const [variableModalOpened, { open: openVariableModal, close: closeVariableModal }] = useDisclosure(false);
    const [editingVariable, setEditingVariable] = useState<ContextVariable | null>(null);

    // Initialize validated form
    const form = useValidatedForm<Partial<ContextData>>({
        schemaName: 'Context' as any,
        initialValues: {
            name: initialData?.name || '',
            description: initialData?.description || '',
            scope: initialData?.scope || 'workflow',
            parentContext: initialData?.parentContext || '',
            variables: initialData?.variables || [],
            inheritanceEnabled: initialData?.inheritanceEnabled !== undefined ? initialData.inheritanceEnabled : true,
            overridePolicy: initialData?.overridePolicy || 'merge',
            validation: initialData?.validation || {
                enabled: false
            },
            metadata: initialData?.metadata || {}
        },
        validateOnChange: true,
        validateOnBlur: true,
        showNotifications: false
    });

    // Form submission with GraphQL
    const { submitForm, loading: submitting, submissionResult } = useValidatedFormSubmission(
        'Context' as any,
        () => ({
            mutate: async (data: Partial<ContextData>) => {
                if (mode === 'create') {
                    return await createContext(data as any);
                } else {
                    return await updateContext(initialData?.name || '', data as any);
                }
            },
            loading: false,
            error: null
        })
    );

    // Scope options
    const scopeOptions = [
        { value: 'global', label: 'Global - Available across all workflows' },
        { value: 'workflow', label: 'Workflow - Scoped to specific workflow definition' },
        { value: 'instance', label: 'Instance - Scoped to workflow instance' },
        { value: 'task', label: 'Task - Scoped to individual task' }
    ];

    // Override policy options
    const overridePolicyOptions = [
        { value: 'allow', label: 'Allow - Child contexts can override parent variables' },
        { value: 'deny', label: 'Deny - Child contexts cannot override parent variables' },
        { value: 'merge', label: 'Merge - Merge parent and child variables intelligently' }
    ];

    // Variable type options
    const variableTypeOptions = [
        { value: 'string', label: 'String' },
        { value: 'number', label: 'Number' },
        { value: 'boolean', label: 'Boolean' },
        { value: 'object', label: 'Object' },
        { value: 'array', label: 'Array' }
    ];

    // Add or edit variable
    const handleVariableSubmit = (variable: ContextVariable) => {
        const currentVariables = form.values.variables || [];

        if (editingVariable) {
            // Update existing variable
            const index = currentVariables.findIndex((v: ContextVariable) => v.name === editingVariable.name);
            if (index >= 0) {
                const updatedVariables = [...currentVariables];
                updatedVariables[index] = variable;
                form.setFieldValue('variables', updatedVariables);
            }
        } else {
            // Add new variable
            form.setFieldValue('variables', [...currentVariables, variable]);
        }

        setEditingVariable(null);
        closeVariableModal();
    };

    // Remove variable
    const removeVariable = (variableName: string) => {
        const updatedVariables = (form.values.variables || []).filter((v: ContextVariable) => v.name !== variableName);
        form.setFieldValue('variables', updatedVariables);
    };

    // Open variable modal for editing
    const openVariableModalForEdit = (variable: ContextVariable) => {
        setEditingVariable(variable);
        openVariableModal();
    };

    // Open variable modal for creation
    const openVariableModalForCreate = () => {
        setEditingVariable(null);
        openVariableModal();
    };

    // Handle form submission
    const handleSubmit = async (values: Partial<ContextData>) => {
        const result = await submitForm(values);
        if (result.success && onSuccess) {
            onSuccess(result.data);
        }
    };

    const getScopeColor = (scope: string) => {
        switch (scope) {
            case 'global': return 'red';
            case 'workflow': return 'blue';
            case 'instance': return 'green';
            case 'task': return 'orange';
            default: return 'gray';
        }
    };

    const getTypeColor = (type: string) => {
        switch (type) {
            case 'string': return 'blue';
            case 'number': return 'green';
            case 'boolean': return 'orange';
            case 'object': return 'purple';
            case 'array': return 'red';
            default: return 'gray';
        }
    };

    return (
        <form onSubmit={form.onSubmit((values) => handleSubmit(values as Partial<ContextData>))}>
            <Stack gap="lg">
                {/* Header */}
                <Paper p="md" withBorder>
                    <Group justify="space-between">
                        <div>
                            <Group>
                                <Title order={3}>
                                    {mode === 'create' ? 'Create' : 'Edit'} Context
                                </Title>
                                <IconVariable size={24} color="blue" />
                            </Group>
                            <Text size="sm" c="dimmed">
                                Configure workflow context and variable scoping with inheritance
                            </Text>
                        </div>
                        <Group>
                            <Badge
                                color={form.validationResult.valid ? 'green' : 'red'}
                                leftSection={form.validationResult.valid ? <IconCheck size={14} /> : <IconX size={14} />}
                            >
                                {form.validationResult.valid ? 'Valid' : `${form.validationResult.errors.length} Error${form.validationResult.errors.length > 1 ? 's' : ''}`}
                            </Badge>
                        </Group>
                    </Group>
                </Paper>

                {/* Validation Summary */}
                {form.validationResult.errors.length > 0 && (
                    <Alert color="red" title="Validation Errors" icon={<IconAlertTriangle />}>
                        <Stack gap="xs">
                            {form.validationResult.errors.map((error, index) => (
                                <Text key={index} size="sm">
                                    <strong>{error.field}:</strong> {error.message}
                                </Text>
                            ))}
                        </Stack>
                    </Alert>
                )}

                {form.validationResult.warnings && form.validationResult.warnings.length > 0 && (
                    <Alert color="yellow" title="Validation Warnings" icon={<IconInfoCircle />}>
                        <Stack gap="xs">
                            {form.validationResult.warnings.map((warning, index) => (
                                <Text key={index} size="sm">
                                    <strong>{warning.field}:</strong> {warning.message}
                                    {warning.suggestion && <Text c="dimmed"> • {warning.suggestion}</Text>}
                                </Text>
                            ))}
                        </Stack>
                    </Alert>
                )}

                {/* Context Form Tabs */}
                <Tabs value={activeTab} onChange={(v) => setActiveTab(v ?? '')}>
                    <Tabs.List>
                        <Tabs.Tab value="basic" leftSection={<IconInfoCircle size={16} />}>
                            Basic Info
                        </Tabs.Tab>
                        <Tabs.Tab value="variables" leftSection={<IconVariable size={16} />}>
                            Variables ({form.values.variables?.length || 0})
                        </Tabs.Tab>
                        <Tabs.Tab value="inheritance" leftSection={<IconHierarchy size={16} />}>
                            Inheritance
                        </Tabs.Tab>
                        <Tabs.Tab value="validation" leftSection={<IconCode size={16} />}>
                            Validation
                        </Tabs.Tab>
                    </Tabs.List>

                    {/* Basic Information Tab */}
                    <Tabs.Panel value="basic">
                        <Card withBorder mt="md">
                            <Stack gap="md">
                                <TextInput
                                    label="Context Name"
                                    placeholder="user_context"
                                    required
                                    {...form.getInputProps('name')}
                                    error={form.getFieldError('name')}
                                    rightSection={
                                        form.isFieldValid('name') && form.values.name ?
                                            <IconCheck size={16} color="green" /> : undefined
                                    }
                                />

                                <Textarea
                                    label="Description"
                                    placeholder="Context for storing user-related workflow variables..."
                                    minRows={3}
                                    {...form.getInputProps('description')}
                                    error={form.getFieldError('description')}
                                />

                                <Select
                                    label="Scope"
                                    data={scopeOptions}
                                    required
                                    {...form.getInputProps('scope')}
                                    error={form.getFieldError('scope')}
                                    rightSection={
                                        <Badge size="xs" color={getScopeColor(form.values.scope || '')}>
                                            {form.values.scope}
                                        </Badge>
                                    }
                                />

                                <TextInput
                                    label="Parent Context"
                                    placeholder="global_context"
                                    description="Name of parent context to inherit variables from"
                                    {...form.getInputProps('parentContext')}
                                    error={form.getFieldError('parentContext')}
                                />
                            </Stack>
                        </Card>
                    </Tabs.Panel>

                    {/* Variables Tab */}
                    <Tabs.Panel value="variables">
                        <Card withBorder mt="md">
                            <Group justify="space-between" mb="md">
                                <Title order={4}>Context Variables</Title>
                                <Button
                                    size="sm"
                                    variant="light"
                                    leftSection={<IconPlus size={16} />}
                                    onClick={openVariableModalForCreate}
                                >
                                    Add Variable
                                </Button>
                            </Group>

                            {form.values.variables && form.values.variables.length > 0 ? (
                                <Table>
                                    <Table.Thead>
                                        <Table.Tr>
                                            <Table.Th>Name</Table.Th>
                                            <Table.Th>Type</Table.Th>
                                            <Table.Th>Value</Table.Th>
                                            <Table.Th>Flags</Table.Th>
                                            <Table.Th>Actions</Table.Th>
                                        </Table.Tr>
                                    </Table.Thead>
                                    <Table.Tbody>
                                        {form.values.variables.map((variable: ContextVariable, index: number) => (
                                            <Table.Tr key={index}>
                                                <Table.Td>
                                                    <Group>
                                                        <Text fw={500}>{variable.name}</Text>
                                                        {variable.description && (
                                                            <Tooltip label={variable.description}>
                                                                <IconInfoCircle size={14} color="gray" />
                                                            </Tooltip>
                                                        )}
                                                    </Group>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Badge size="sm" color={getTypeColor(variable.type)}>
                                                        {variable.type}
                                                    </Badge>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Code>
                                                        {variable.encrypted ? '***encrypted***' :
                                                            typeof variable.value === 'object' ?
                                                                JSON.stringify(variable.value) :
                                                                String(variable.value)}
                                                    </Code>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Group gap={4}>
                                                        {variable.required && <Badge size="xs" color="red">Required</Badge>}
                                                        {variable.readonly && <Badge size="xs" color="orange">Readonly</Badge>}
                                                        {variable.encrypted && <Badge size="xs" color="blue">Encrypted</Badge>}
                                                    </Group>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Group gap={4}>
                                                        <ActionIcon
                                                            size="sm"
                                                            variant="subtle"
                                                            onClick={() => openVariableModalForEdit(variable)}
                                                        >
                                                            <IconCode size={14} />
                                                        </ActionIcon>
                                                        <ActionIcon
                                                            size="sm"
                                                            variant="subtle"
                                                            color="red"
                                                            onClick={() => removeVariable(variable.name)}
                                                        >
                                                            <IconTrash size={14} />
                                                        </ActionIcon>
                                                    </Group>
                                                </Table.Td>
                                            </Table.Tr>
                                        ))}
                                    </Table.Tbody>
                                </Table>
                            ) : (
                                <Text c="dimmed" ta="center" py="xl">
                                    No variables defined. Add variables to store workflow data.
                                </Text>
                            )}
                        </Card>
                    </Tabs.Panel>

                    {/* Inheritance Tab */}
                    <Tabs.Panel value="inheritance">
                        <Card withBorder mt="md">
                            <Stack gap="md">
                                <Title order={4}>Inheritance Configuration</Title>

                                <Checkbox
                                    label="Enable Inheritance"
                                    description="Allow this context to inherit variables from parent contexts"
                                    {...form.getInputProps('inheritanceEnabled', { type: 'checkbox' })}
                                />

                                {form.values.inheritanceEnabled && (
                                    <Select
                                        label="Override Policy"
                                        description="How to handle variable conflicts between parent and child contexts"
                                        data={overridePolicyOptions}
                                        {...form.getInputProps('overridePolicy')}
                                        error={form.getFieldError('overridePolicy')}
                                    />
                                )}

                                {form.values.parentContext && (
                                    <Alert color="blue" title="Parent Context" icon={<IconHierarchy />}>
                                        <Text>
                                            This context will inherit variables from: <strong>{form.values.parentContext}</strong>
                                        </Text>
                                        <Text size="sm" c="dimmed" mt={4}>
                                            Override policy: <strong>{form.values.overridePolicy}</strong>
                                        </Text>
                                    </Alert>
                                )}
                            </Stack>
                        </Card>
                    </Tabs.Panel>

                    {/* Validation Tab */}
                    <Tabs.Panel value="validation">
                        <Card withBorder mt="md">
                            <Stack gap="md">
                                <Group justify="space-between">
                                    <Title order={4}>Variable Validation</Title>
                                    <Checkbox
                                        label="Enable Validation"
                                        checked={form.values.validation?.enabled || false}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => form.setFieldValue('validation.enabled', e.currentTarget.checked)}
                                    />
                                </Group>

                                {form.values.validation?.enabled && (
                                    <JsonInput
                                        label="Validation Schema (JSON Schema)"
                                        placeholder='{ "type": "object", "properties": { "userId": { "type": "string" } } }'
                                        validationError="Invalid JSON Schema"
                                        formatOnBlur
                                        autosize
                                        minRows={6}
                                        maxRows={12}
                                        value={typeof form.values.validation.schema === 'string' ?
                                            form.values.validation.schema :
                                            JSON.stringify(form.values.validation.schema || {}, null, 2)
                                        }
                                        onChange={(value) => {
                                            try {
                                                const parsed = JSON.parse(value || '{}');
                                                form.setFieldValue('validation.schema', parsed);
                                            } catch {
                                                form.setFieldValue('validation.schema', value);
                                            }
                                        }}
                                        error={form.getFieldError('validation.schema')}
                                    />
                                )}

                                <JsonInput
                                    label="Additional Metadata (JSON)"
                                    placeholder='{ "category": "user_data", "owner": "user_service" }'
                                    validationError="Invalid JSON"
                                    formatOnBlur
                                    autosize
                                    minRows={4}
                                    maxRows={8}
                                    value={typeof form.values.metadata === 'string' ?
                                        form.values.metadata :
                                        JSON.stringify(form.values.metadata || {}, null, 2)
                                    }
                                    onChange={(value) => {
                                        try {
                                            const parsed = JSON.parse(value || '{}');
                                            form.setFieldValue('metadata', parsed);
                                        } catch {
                                            form.setFieldValue('metadata', value);
                                        }
                                    }}
                                    error={form.getFieldError('metadata')}
                                />
                            </Stack>
                        </Card>
                    </Tabs.Panel>
                </Tabs>

                {/* Form Actions */}
                <Paper p="md" withBorder>
                    <Group justify="space-between">
                        <div>
                            <Text size="sm" c="dimmed">
                                Context will be validated before submission
                            </Text>
                            {form.isValidating && (
                                <Text size="sm" c="blue">
                                    Validating...
                                </Text>
                            )}
                        </div>

                        <Group>
                            <Button variant="light" onClick={onCancel}>
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                loading={submitting || form.isValidating}
                                disabled={!form.validationResult.valid}
                            >
                                {mode === 'create' ? 'Create Context' : 'Update Context'}
                            </Button>
                        </Group>
                    </Group>
                </Paper>

                {/* Submission Result */}
                {submissionResult && (
                    <Alert
                        color={submissionResult.success ? 'green' : 'red'}
                        title={submissionResult.success ? 'Success' : 'Error'}
                    >
                        {submissionResult.success ?
                            'Context saved successfully!' :
                            submissionResult.error
                        }
                    </Alert>
                )}

                {/* Variable Modal */}
                <Modal
                    opened={variableModalOpened}
                    onClose={closeVariableModal}
                    title={editingVariable ? 'Edit Variable' : 'Add Variable'}
                    size="md"
                >
                    <VariableForm
                        initialData={editingVariable}
                        onSubmit={handleVariableSubmit}
                        onCancel={closeVariableModal}
                    />
                </Modal>
            </Stack>
        </form>
    );
};

// Variable form component
const VariableForm: React.FC<{
    initialData?: ContextVariable | null;
    onSubmit: (variable: ContextVariable) => void;
    onCancel: () => void;
}> = ({ initialData, onSubmit, onCancel }) => {
    const [formData, setFormData] = useState<ContextVariable>(
        initialData || {
            name: '',
            type: 'string',
            value: '',
            required: false,
            readonly: false,
            encrypted: false
        }
    );

    const handleSubmit = () => {
        if (!formData.name.trim()) {
            notifications.show({
                title: 'Validation Error',
                message: 'Variable name is required',
                color: 'red'
            });
            return;
        }

        onSubmit(formData);
    };

    const handleValueChange = (value: string) => {
        try {
            // Try to parse as JSON for object/array types
            if (formData.type === 'object' || formData.type === 'array') {
                const parsed = JSON.parse(value);
                setFormData(prev => ({ ...prev, value: parsed }));
            } else if (formData.type === 'number') {
                const num = parseFloat(value);
                setFormData(prev => ({ ...prev, value: isNaN(num) ? value : num }));
            } else if (formData.type === 'boolean') {
                setFormData(prev => ({ ...prev, value: value.toLowerCase() === 'true' }));
            } else {
                setFormData(prev => ({ ...prev, value }));
            }
        } catch {
            // If JSON parsing fails, store as string
            setFormData(prev => ({ ...prev, value }));
        }
    };

    const getValueAsString = () => {
        if (typeof formData.value === 'object') {
            return JSON.stringify(formData.value, null, 2);
        }
        return String(formData.value);
    };

    return (
        <Stack gap="md">
            <TextInput
                label="Variable Name"
                placeholder="userId"
                required
                value={formData.name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, name: e.currentTarget.value }))}
            />

            <Textarea
                label="Description"
                placeholder="User identifier for workflow context"
                value={formData.description || ''}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFormData(prev => ({ ...prev, description: e.currentTarget.value }))}
                minRows={2}
            />

            <Select
                label="Type"
                data={[
                    { value: 'string', label: 'String' },
                    { value: 'number', label: 'Number' },
                    { value: 'boolean', label: 'Boolean' },
                    { value: 'object', label: 'Object' },
                    { value: 'array', label: 'Array' }
                ]}
                value={formData.type}
                onChange={(value: string | null) => setFormData(prev => ({ ...prev, type: value as any }))}
            />

            {formData.type === 'object' || formData.type === 'array' ? (
                <JsonInput
                    label="Value (JSON)"
                    placeholder={formData.type === 'object' ? '{}' : '[]'}
                    value={getValueAsString()}
                    onChange={handleValueChange}
                    minRows={3}
                    maxRows={6}
                />
            ) : (
                <TextInput
                    label="Value"
                    placeholder={
                        formData.type === 'boolean' ? 'true or false' :
                            formData.type === 'number' ? '123' : 'value'
                    }
                    value={getValueAsString()}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleValueChange(e.currentTarget.value)}
                />
            )}

            <Group>
                <Checkbox
                    label="Required"
                    checked={formData.required}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, required: e.currentTarget.checked }))}
                />
                <Checkbox
                    label="Readonly"
                    checked={formData.readonly}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, readonly: e.currentTarget.checked }))}
                />
                <Checkbox
                    label="Encrypted"
                    checked={formData.encrypted}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, encrypted: e.currentTarget.checked }))}
                />
            </Group>

            <Group justify="flex-end" mt="md">
                <Button variant="light" onClick={onCancel}>
                    Cancel
                </Button>
                <Button onClick={handleSubmit}>
                    {initialData ? 'Update' : 'Add'} Variable
                </Button>
            </Group>
        </Stack>
    );
};

export default ValidatedContextForm;